import { ApolloError } from '@apollo/client'
import { Select, Table } from 'antd'
import classNames from 'classnames'
import { FC, ReactNode, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PerIcon from '../../../components/custom_components/Icon'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import { PerTableActionButtons } from '../../../components/custom_components/TableActionButtons'
import PerText from '../../../components/custom_components/Text'
import { useAuth } from '../../../context/auth-context'
import {
  CompanyManagerAppraisalMeetingStatus,
  CompanyManagerAppraisalMeetingStatusTableRowDto,
  Country,
  EmployeeLevelEnum,
  MeetingPdfStatus,
  RoleName,
} from '../../../types'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import {
  appraisalMeetingStatusToDashboardIcon,
  appraisalMonitoringStatusIconColor,
  AppraisalMonitoringTableStatusFilter,
  hasAppraisalMonitoringFilterAreaValues,
  MONITORING_TABLE_STATUS_OPTIONS,
  visibleMonitoringStatusOptions,
} from '../utils/appraisalMeetingStatusDashboardUtil'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import { useDateTime } from '../../../shared/useDateTime'
import { getSortOrderByEmployeeLevel } from '../../companySettings/employees/employeesLevelSorterUtil'
import { ForceConfirmationModal, ForceConfirmationRefetchContext } from '../../meeting/ForceConfirmationModal'
import { useDashboardStore } from '../DashboardStore'
import { MeetingStatusModal, MeetingStatusModalData } from '../../../components/custom_components/MeetingStatusModal'
import { appraisalTableUiProps } from '../../companySettings/appraisal/appraisalTable.uiprops'
import { getConclusionHrefByMeeting } from '../../meeting/util/hrefUtils'
import { DashboardAppraisalInviteModalContainer } from '../../companySettings/appraisal/DashboardAppraisalInviteModalContainer'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { PerAvatarDashboardMonitoringTable } from '../../../components/custom_components/AvatarPlus/PerAvatarDashboardMonitoringTable'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import DashboardAppraisalMonitoringActiveFilters from './DashboardAppraisalMonitoringActiveFilters'

const { Option } = Select

type ForceConfirmationModalPayload = {
  meetingId: string
  country: Country
  employeeFirstName: string
}

const DashboardAppraisalMonitoringTableTabBody: FC<{
  employeeLevels: EmployeeLevelEnum[]
  companyId: string
  loading: boolean
  error?: ApolloError
}> = ({ employeeLevels, companyId, loading, error }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateShort, formatDateTimeShort } = useDateTime(user)
  const namePreference = user.nameDisplayPreference as NameDisplayPreference
  const { pdfBtnTooltip } = appraisalTableUiProps(t, formatDateShort, formatDateTimeShort)
  const [
    userDashboardRoles,
    appraisalMonitoringFilters,
    appraisalMonitoringFilteredRows,
    appraisalMonitoringAllRows,
    appraisalMonitoringTableStatus,
    setAppraisalMonitoringTableStatus,
    clearAppraisalMonitoringFilters,
  ] = useDashboardStore((state) => [
    state.selectedDashboardRoles,
    state.appraisalMonitoringFilters,
    state.appraisalMonitoringFilteredRows,
    state.appraisalMonitoringAllRows,
    state.appraisalMonitoringTableStatus,
    state.setAppraisalMonitoringTableStatus,
    state.clearAppraisalMonitoringFilters,
  ])

  const hasAppraisalMonitoringFilterAreaApplied = useMemo(
    () => hasAppraisalMonitoringFilterAreaValues(appraisalMonitoringFilters),
    [appraisalMonitoringFilters],
  )
  const hasAppraisalMonitoringTableStatusFilterApplied = useMemo(() => appraisalMonitoringTableStatus, [appraisalMonitoringTableStatus])

  const visibleStatusFilterOptions = useMemo(() => {
    const counts = new Map<AppraisalMonitoringTableStatusFilter, number>()
    for (const status of MONITORING_TABLE_STATUS_OPTIONS) {
      counts.set(status, 0)
    }
    for (const row of appraisalMonitoringAllRows) {
      counts.set(row.status, (counts.get(row.status) ?? 0) + 1)
    }
    return visibleMonitoringStatusOptions(counts)
  }, [appraisalMonitoringAllRows])

  const renderMonitoringStatusOptionLabel = useCallback(
    (status: AppraisalMonitoringTableStatusFilter): ReactNode => (
      <PerFlexRow align="middle" noWrap>
        <PerIcon
          name={appraisalMeetingStatusToDashboardIcon(status)}
          size={18}
          variant="decoration"
          color={appraisalMonitoringStatusIconColor(status)}
        />
        <div style={{ width: '80%' }}>
          <PerText fontSize="xs" ml="xxxs" className="truncate-text" color="secondary">
            {t(`dashboard.companyManager.monitoring.chart.status.${status}`)}
          </PerText>
        </div>
      </PerFlexRow>
    ),
    [t],
  )

  const statusFilterSelectValue = useMemo(() => {
    if (!appraisalMonitoringTableStatus) {
      return undefined
    }
    return {
      key: appraisalMonitoringTableStatus,
      label: renderMonitoringStatusOptionLabel(appraisalMonitoringTableStatus),
    }
  }, [appraisalMonitoringTableStatus, renderMonitoringStatusOptionLabel])

  const [employeeToInvite, setEmployeeToInvite] = useState<string | null>(null)
  const [forceConfirmationPayload, setForceConfirmationPayload] = useState<ForceConfirmationModalPayload | null>(null)
  const [meetingStatusModalData, setMeetingStatusModalData] = useState<MeetingStatusModalData | null>(null)

  const rows = useMemo(() => {
    if (!!appraisalMonitoringTableStatus) {
      return appraisalMonitoringFilteredRows.filter((r) => r.status === appraisalMonitoringTableStatus)
    }
    return appraisalMonitoringFilteredRows
  }, [appraisalMonitoringFilteredRows, appraisalMonitoringTableStatus])
  const hasAnyOffice = useMemo(() => rows.some((r) => !!r.office), [rows])

  const userIsHRAdminOfCountry = useCallback(
    (country: Country) => {
      const hrAdminRoles = user.roles?.filter((rn) => rn.includes('HR_ADMIN')) || []
      return hrAdminRoles.some((rn) => rn.split('_')[2] === country)
    },
    [user.roles],
  )

  const columns = useMemo(() => {
    const width = (base: number, increment: number) => (hasAnyOffice ? base : base + increment)
    return [
      {
        key: 'status',
        title: t('dashboard.companyManager.monitoring.table.columns.status'),
        className: 'textAlign-center',
        width: '8%',
        render: (_text: unknown, row: CompanyManagerAppraisalMeetingStatusTableRowDto) => (
          <PerIcon
            name={appraisalMeetingStatusToDashboardIcon(row.status)}
            size={24}
            variant="decoration"
            color={appraisalMonitoringStatusIconColor(row.status)}
            tooltip={t(`dashboard.companyManager.monitoring.chart.status.${row.status}`)}
          />
        ),
      },
      {
        key: 'employee',
        title: t('dashboard.companyManager.monitoring.table.columns.employee'),
        className: 'textAlign-left',
        width: `${width(19, 4)}%`,
        sorter: (a: CompanyManagerAppraisalMeetingStatusTableRowDto, b: CompanyManagerAppraisalMeetingStatusTableRowDto) => {
          const nameA = computeFullname(namePreference, a.employee.firstName, a.employee.lastName)
          const nameB = computeFullname(namePreference, b.employee.firstName, b.employee.lastName)
          return nameA.localeCompare(nameB)
        },
        render: (_text: unknown, row: CompanyManagerAppraisalMeetingStatusTableRowDto) => (
          <PerAvatarDashboardMonitoringTable
            nameDisplayPreference={namePreference}
            employeeId={row.employee.id}
            firstName={row.employee.firstName}
            lastName={row.employee.lastName}
          />
        ),
      },
      {
        key: 'replyEntryDate',
        title: t('dashboard.companyManager.monitoring.table.columns.replyEntryDate'),
        className: 'textAlign-left',
        width: `${width(13, 4)}%`,
        sorter: (a: CompanyManagerAppraisalMeetingStatusTableRowDto, b: CompanyManagerAppraisalMeetingStatusTableRowDto) =>
          String(a.replyEntryDate).localeCompare(String(b.replyEntryDate)),
        render: (_text: unknown, row: CompanyManagerAppraisalMeetingStatusTableRowDto) => (
          <PerText color="secondary" fontSize="xs">
            {formatDateShort(row.replyEntryDate)}
          </PerText>
        ),
      },
      {
        key: 'buManagers',
        title: t('dashboard.companyManager.monitoring.table.columns.buManagers'),
        className: 'textAlign-left',
        width: `${width(18, 4)}%`,
        render: (_text: unknown, row: CompanyManagerAppraisalMeetingStatusTableRowDto) =>
          row.buManagers.length === 0 ? (
            <PerText color="secondary" fontSize="xs">
              -
            </PerText>
          ) : (
            <div className="dashboard-appraisal-monitoring__bu-managers">
              {row.buManagers.map((m) => (
                <PerFlexRow align="middle" noWrap>
                  <PerText key={m.id} fontSize="xs" className="truncate-text">
                    {computeFullname(namePreference, m.firstName, m.lastName)}
                  </PerText>
                </PerFlexRow>
              ))}
            </div>
          ),
      },
      {
        key: 'businessUnit',
        title: t('dashboard.companyManager.monitoring.table.columns.bu'),
        className: 'textAlign-left',
        width: '11%',
        sorter: (a: CompanyManagerAppraisalMeetingStatusTableRowDto, b: CompanyManagerAppraisalMeetingStatusTableRowDto) =>
          a.businessUnit.displayName.localeCompare(b.businessUnit.displayName),
        render: (_text: unknown, row: CompanyManagerAppraisalMeetingStatusTableRowDto) => (
          <PerText fontSize="xs">{row.businessUnit.displayName}</PerText>
        ),
      },
      ...(hasAnyOffice
        ? [
            {
              key: 'office',
              title: t('dashboard.companyManager.monitoring.table.columns.office'),
              className: 'textAlign-left',
              width: '12%',
              sorter: (a: CompanyManagerAppraisalMeetingStatusTableRowDto, b: CompanyManagerAppraisalMeetingStatusTableRowDto) => {
                const officeLabel = (r: CompanyManagerAppraisalMeetingStatusTableRowDto) => (r.office ? `${r.office.code}` : '')
                return officeLabel(a).localeCompare(officeLabel(b))
              },
              render: (_text: unknown, row: CompanyManagerAppraisalMeetingStatusTableRowDto) => (
                <PerText color="secondary" fontSize="xs">
                  {row.office ? row.office.code : '-'}
                </PerText>
              ),
            } as const,
          ]
        : []),
      {
        key: 'level',
        title: t('dashboard.companyManager.monitoring.table.columns.level'),
        className: 'textAlign-center',
        width: '9%',
        sorter: (a: CompanyManagerAppraisalMeetingStatusTableRowDto, b: CompanyManagerAppraisalMeetingStatusTableRowDto) => {
          const levelA = getSortOrderByEmployeeLevel(a.employee.currentLevel as EmployeeLevelEnum, employeeLevels)
          const levelB = getSortOrderByEmployeeLevel(b.employee.currentLevel as EmployeeLevelEnum, employeeLevels)
          return levelA - levelB
        },
        render: (_text: unknown, row: CompanyManagerAppraisalMeetingStatusTableRowDto) => (
          <PerText fontSize="xs">{row.employee.currentLevel}</PerText>
        ),
      },
      {
        key: 'actions',
        title: t('dashboard.companyManager.monitoring.table.columns.actions'),
        className: 'textAlign-center',
        width: '10%',
        render: (_text: unknown, row: CompanyManagerAppraisalMeetingStatusTableRowDto) => {
          const conclusionPdfStatus = row.confirmedOrLatestMeetingConclusionPdf?.status
          return (
            <PerTableActionButtons
              variant="pastel"
              justify="start"
              buttons={[
                {
                  showOnlyWhen:
                    row.status === CompanyManagerAppraisalMeetingStatus.WaitingForEmployeeConfirmation &&
                    !!row.confirmedOrLatestMeetingId &&
                    (userDashboardRoles.includes(RoleName.CompanyManager) ||
                      (userDashboardRoles.includes(RoleName.Hr) && userIsHRAdminOfCountry(row.country))),
                  disabled: conclusionPdfStatus !== MeetingPdfStatus.FinishedSuccess,
                  icon: 'chat_stroke',
                  color: 'info',
                  size: 'small',
                  iconVariant: 'decoration',
                  tooltip: t('managerMeetings.tooltip.forceConfirmation'),
                  onClick: () => {
                    setForceConfirmationPayload({
                      meetingId: String(row.confirmedOrLatestMeetingId),
                      country: row.country,
                      employeeFirstName: row.employee.firstName,
                    })
                  },
                },
                {
                  showOnlyWhen:
                    [
                      CompanyManagerAppraisalMeetingStatus.NotPrepared,
                      CompanyManagerAppraisalMeetingStatus.Prepared,
                      CompanyManagerAppraisalMeetingStatus.Outdated,
                    ].includes(row.status) &&
                    !!row.confirmedOrLatestMeetingId &&
                    !!row.confirmedOrLatestMeetingType,
                  icon: 'view_fill',
                  color: 'warning',
                  size: 'small',
                  iconVariant: 'decoration',
                  tooltip: t('appraisalStatusModal.tooltip'),
                  onClick: () =>
                    setMeetingStatusModalData({
                      employeeId: row.employee.id,
                      meetingId: String(row.confirmedOrLatestMeetingId),
                      meetingType: row.confirmedOrLatestMeetingType!,
                      country: row.country,
                    }),
                },
                {
                  showOnlyWhen: [
                    CompanyManagerAppraisalMeetingStatus.Done,
                    CompanyManagerAppraisalMeetingStatus.WaitingForEmployeeConfirmation,
                    CompanyManagerAppraisalMeetingStatus.HavingAnAppraisalUpdate,
                    CompanyManagerAppraisalMeetingStatus.WaitingForHrResult,
                  ].includes(row.status),
                  icon: 'view_fill',
                  color: 'primary',
                  size: 'small',
                  iconVariant: 'decoration',
                  tooltip: conclusionPdfStatus ? pdfBtnTooltip(conclusionPdfStatus) : undefined,
                  asLink:
                    conclusionPdfStatus === MeetingPdfStatus.FinishedSuccess
                      ? getConclusionHrefByMeeting({
                          id: String(row.confirmedOrLatestMeetingId),
                          conclusionPdfFileName: row.confirmedOrLatestMeetingConclusionPdf!.fileName,
                          conclusionPdfDownloadToken: row.confirmedOrLatestMeetingConclusionPdf!.downloadToken,
                          conclusionPdfStatus: row.confirmedOrLatestMeetingConclusionPdf!.status,
                        })
                      : undefined,
                  disabled: !(conclusionPdfStatus === MeetingPdfStatus.FinishedSuccess),
                },
                {
                  showOnlyWhen:
                    [CompanyManagerAppraisalMeetingStatus.NotInvited].includes(row.status) &&
                    userDashboardRoles.includes(RoleName.CompanyManager),
                  icon: 'invite_fill',
                  color: 'primary',
                  size: 'small',
                  iconVariant: 'decoration',
                  tooltip: t('meetingInvite.headline'),
                  onClick: (e: any) => {
                    e.preventDefault()
                    setEmployeeToInvite(row.employee.id)
                  },
                },
              ]}
            />
          )
        },
      },
    ]
  }, [formatDateShort, hasAnyOffice, namePreference, t, employeeLevels, pdfBtnTooltip, userDashboardRoles, userIsHRAdminOfCountry])

  return (
    <>
      {forceConfirmationPayload && (
        <ForceConfirmationModal
          companyId={companyId}
          country={forceConfirmationPayload.country}
          meetingId={forceConfirmationPayload.meetingId}
          onClose={() => setForceConfirmationPayload(null)}
          employeeFirstName={forceConfirmationPayload.employeeFirstName}
          refetchContext={ForceConfirmationRefetchContext.CompanyManagerDashboardMonitoring}
          userDashboardRoles={userDashboardRoles}
        />
      )}
      {meetingStatusModalData && (
        <MeetingStatusModal
          employeeId={meetingStatusModalData.employeeId}
          meetingId={meetingStatusModalData.meetingId}
          meetingType={meetingStatusModalData.meetingType}
          onClose={() => {
            setMeetingStatusModalData(null)
          }}
          country={meetingStatusModalData.country}
        />
      )}
      {employeeToInvite && (
        <DashboardAppraisalInviteModalContainer
          employeeId={employeeToInvite}
          closeModal={() => {
            setEmployeeToInvite(null)
          }}
        />
      )}
      <div className="dashboard-appraisal-monitoring__tab-body dashboard-appraisal-monitoring__table-tab">
        <>
          <DashboardAppraisalMonitoringActiveFilters />
          <PerFlexRow align="middle" justify="start" mb="m">
            <PerFlexRow align="middle" mr="l">
              <PerText fontSize="xs" color="secondary" mr="xs">
                {t('dashboard.companyManager.monitoring.table.clearFilters')}
              </PerText>
              <PerButtonCircle
                type="button"
                size="small"
                icon="delete_fill"
                tooltip={t('dashboard.companyManager.monitoring.table.clearFilters')}
                variant="pastel"
                disabled={!hasAppraisalMonitoringFilterAreaApplied && !hasAppraisalMonitoringTableStatusFilterApplied}
                onClick={() => {
                  setAppraisalMonitoringTableStatus(undefined)
                  clearAppraisalMonitoringFilters()
                }}
              />
            </PerFlexRow>
            <PerFlexRow align="middle">
              <div style={{ width: '190px' }}>
                <Select
                  allowClear
                  size="small"
                  style={{ width: '100%' }}
                  className={classNames('dashboard-appraisal-monitoring__status-filter-select', {
                    'dashboard-appraisal-monitoring__status-filter-select--active': !!appraisalMonitoringTableStatus,
                  })}
                  dropdownClassName="dashboard-appraisal-monitoring__status-filter-dropdown"
                  labelInValue
                  placeholder={t('dashboard.companyManager.monitoring.table.filterByStatus')}
                  value={statusFilterSelectValue}
                  onChange={(opt) => {
                    if (opt && typeof opt === 'object' && 'key' in opt) {
                      setAppraisalMonitoringTableStatus(opt.key)
                      return
                    }
                    setAppraisalMonitoringTableStatus(undefined)
                  }}
                >
                  {visibleStatusFilterOptions
                    .filter((status) => status !== statusFilterSelectValue?.key)
                    .map((status) => (
                      <Option key={status} value={status} title={t(`dashboard.companyManager.monitoring.chart.status.${status}`)}>
                        {renderMonitoringStatusOptionLabel(status)}
                      </Option>
                    ))}
                </Select>
              </div>
            </PerFlexRow>
          </PerFlexRow>
          <div className="dashboard-appraisal-monitoring__table-region">
            {!!error || !employeeLevels.length || loading ? (
              <div className="dashboard-appraisal-monitoring__table-region-loading">
                <NewLoadingAndErrors t={t} isLoading={loading} hasNoData={!employeeLevels.length} error={error} showSpinner />
              </div>
            ) : (
              <Table
                className="fullWidth small"
                rowKey={(row) => String(row.employee.id)}
                columns={columns}
                dataSource={rows}
                pagination={false}
                locale={{
                  emptyText: t('dashboard.companyManager.monitoring.table.empty'),
                }}
              />
            )}
          </div>
        </>
      </div>
    </>
  )
}

export default DashboardAppraisalMonitoringTableTabBody
